/**
 * InputsValidations.js
 * Validaciones para los campos de entrada del formulario de business information
 */

/**
 * Valida que el campo no sea null, vacío y no exceda el límite de caracteres
 * @param {string} value - Valor a validar
 * @param {number} maxLength - Longitud máxima permitida
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateRequiredString = (value, maxLength = 255, fieldName = 'Field') => {
  if (value === null || value === undefined) {
    return {
      isValid: false,
      message: `${fieldName} cannot be null or undefined`
    };
  }
  
  if (value.trim() === '') {
    return {
      isValid: false,
      message: `${fieldName} cannot be empty`
    };
  }
  
  if (value.length > maxLength) {
    return {
      isValid: false,
      message: `${fieldName} cannot exceed ${maxLength} characters`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Formatea el Federal Tax ID (XX-XXXXXXX) y previene edición en medio del campo
 * @param {string} value - Valor a formatear
 * @param {string} currentValue - Valor actual del campo
 * @returns {string} - Valor formateado
 */
export const formatFederalTaxId = (value, currentValue = '') => {
  // Si el campo está vacío, permitir entrada
  if (!currentValue || currentValue.trim() === '') {
    const digits = value.replace(/\D/g, '');
    return digits;
  }
  
  // Si el usuario está borrando, permitir
  if (value.length < currentValue.length) {
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    
    if (digits.length >= 2) {
      formatted = digits.substring(0, 2);
      if (digits.length >= 9) {
        formatted += '-' + digits.substring(2, 9);
      } else if (digits.length > 2) {
        formatted += '-' + digits.substring(2);
      }
    } else {
      formatted = digits;
    }
    return formatted;
  }
  
  // Si el usuario está escribiendo, solo permitir al final
  if (value.length > currentValue.length) {
    // Solo agregar el último carácter si es un número
    const lastChar = value[value.length - 1];
    if (/\d/.test(lastChar)) {
      const newValue = currentValue + lastChar;
      const digits = newValue.replace(/\D/g, '');
      
      let formatted = '';
      if (digits.length >= 2) {
        formatted = digits.substring(0, 2);
        if (digits.length >= 9) {
          formatted += '-' + digits.substring(2, 9);
        } else if (digits.length > 2) {
          formatted += '-' + digits.substring(2);
        }
      } else {
        formatted = digits;
      }
      return formatted;
    }
  }
  
  return currentValue;
};

/**
 * Valida el formato del Federal Tax ID (2 caracteres antes del "-" y 7 después)
 * @param {string} value - Valor a validar
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateFederalTaxId = (value) => {
  if (value === null || value === undefined) {
    return {
      isValid: false,
      message: 'Federal Tax ID cannot be null or undefined'
    };
  }
  
  if (value.trim() === '') {
    return {
      isValid: false,
      message: 'Federal Tax ID cannot be empty'
    };
  }
  
  // Patrón: 2 caracteres antes del "-" y 7 después, solo números
  const taxIdPattern = /^\d{2}-\d{7}$/;
  
  if (!taxIdPattern.test(value)) {
    return {
      isValid: false,
      message: 'Federal Tax ID must be in format XX-XXXXXXX (2 digits, dash, 7 digits)'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida el formato de URL del sitio web
 * @param {string} value - Valor a validar
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateWebsite = (value) => {
  // Si el valor es null, undefined o vacío, es válido (campo no requerido)
  if (value === null || value === undefined || value.trim() === '') {
    return {
      isValid: true,
      message: ''
    };
  }
  
  // Patrón: debe empezar con "www." y terminar con al menos 2 caracteres después del punto
  const websitePattern = /^www\.[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!websitePattern.test(value)) {
    return {
      isValid: false,
      message: 'Website must start with "www." and end with a valid domain (minimum 2 characters after the last dot)'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida que se haya seleccionado una opción de un select
 * @param {string} value - Valor seleccionado
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateSelectOption = (value, fieldName = 'Field') => {
  if (value === null || value === undefined || value === '') {
    return {
      isValid: false,
      message: `Please select a ${fieldName}`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida el Street Address 2 (opcional, máximo 255 caracteres)
 * @param {string} value - Valor a validar
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateStreetAddress2 = (value) => {
  // Campo opcional, si está vacío es válido
  if (value === null || value === undefined || value.trim() === '') {
    return {
      isValid: true,
      message: ''
    };
  }
  
  // Máximo 255 caracteres
  if (value.length > 255) {
    return {
      isValid: false,
      message: 'Street Address 2 cannot exceed 255 characters'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Valida nombres (First Name, Middle Name, Last Name)
 * @param {string} value - Valor a validar
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @param {boolean} isRequired - Si el campo es obligatorio
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateOwnerName = (value, fieldName = 'Name', isRequired = true) => {
  // Si es requerido, validar que no sea null o vacío
  if (isRequired) {
    if (value === null || value === undefined) {
      return {
        isValid: false,
        message: `${fieldName} cannot be null or undefined`
      };
    }
    
    if (value.trim() === '') {
      return {
        isValid: false,
        message: `${fieldName} cannot be empty`
      };
    }
  } else {
    // Si no es requerido y está vacío, es válido
    if (value === null || value === undefined || value.trim() === '') {
      return {
        isValid: true,
        message: ''
      };
    }
  }
  
  // Validar longitud máxima de 255 caracteres PRIMERO
  if (value.length > 255) {
    return {
      isValid: false,
      message: `${fieldName} cannot exceed 255 characters`
    };
  }
  
  // Validar que solo contenga letras, espacios y guiones (para nombres compuestos)
  const namePattern = /^[A-Za-zÀ-ÿ\s\-']+$/;
  if (!namePattern.test(value)) {
    return {
      isValid: false,
      message: `${fieldName} can only contain letters, spaces, hyphens and apostrophes`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Filtra caracteres no permitidos para nombres (solo letras, espacios, guiones y apóstrofes)
 * @param {string} value - Valor a filtrar
 * @returns {string} - Valor filtrado
 */
export const filterOwnerName = (value) => {
  if (!value) return '';
  
  // Solo permitir letras (incluyendo acentos), espacios, guiones y apóstrofes
  let filtered = value.replace(/[^A-Za-zÀ-ÿ\s\-']/g, '');
  
  // Limitar a máximo 255 caracteres
  if (filtered.length > 255) {
    filtered = filtered.substring(0, 255);
  }
  
  return filtered;
};

/**
 * Formatea el nombre del propietario (aplica el filtro)
 * @param {string} value - Valor a formatear
 * @returns {string} - Valor formateado
 */
export const formatOwnerName = (value) => {
  return filterOwnerName(value);
};

/**
 * Valida la fecha de nacimiento (no puede ser mayor que la fecha actual y debe ser mínimo 18 años)
 * @param {string} value - Valor de fecha a validar
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateBirthDate = (value, fieldName = 'Birth Date') => {
  if (value === null || value === undefined || value.trim() === '') {
    return {
      isValid: false,
      message: `${fieldName} cannot be empty`
    };
  }
  
  // Patrón: YYYY-MM-DD
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  
  if (!datePattern.test(value)) {
    return {
      isValid: false,
      message: `${fieldName} must be in format YYYY-MM-DD`
    };
  }
  
  // Validar que la fecha sea válida
  const [year, month, day] = value.split('-').map(Number);
  const inputDate = new Date(year, month - 1, day); // month - 1 porque Date usa 0-11
  
  if (isNaN(inputDate.getTime())) {
    return {
      isValid: false,
      message: `${fieldName} must be a valid date`
    };
  }
  
  // Validar que no sea mayor que la fecha actual
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999); // Establecer al final del día actual
  
  if (inputDate > currentDate) {
    return {
      isValid: false,
      message: `${fieldName} cannot be greater than the current date`
    };
  }
  
  // Validar que sea mínimo 18 años
  const today = new Date();
  const age = today.getFullYear() - inputDate.getFullYear();
  const monthDiff = today.getMonth() - inputDate.getMonth();
  
  // Ajustar la edad si el cumpleaños aún no ha llegado este año
  const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < inputDate.getDate()) 
    ? age - 1 
    : age;
  
  if (actualAge < 18) {
    return {
      isValid: false,
      message: `${fieldName} must be at least 18 years old`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Calcula la fecha máxima permitida para que una persona tenga al menos 18 años
 * @returns {string} - Fecha en formato YYYY-MM-DD
 */
export const getMaxBirthDate = () => {
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  
  const year = maxDate.getFullYear();
  const month = String(maxDate.getMonth() + 1).padStart(2, '0');
  const day = String(maxDate.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Valida la ciudadanía (solo letras, máximo 10 caracteres)
 * @param {string} value - Valor a validar
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateCitizenship = (value, fieldName = 'Citizenship') => {
  if (value === null || value === undefined || value.trim() === '') {
    return {
      isValid: false,
      message: `${fieldName} cannot be empty`
    };
  }

  if (value.length > 25) {
    return {
      isValid: false,
      message: `${fieldName} must be at most 25 letters`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Filtra caracteres no permitidos para ciudadanía (solo letras)
 * @param {string} value - Valor a filtrar
 * @returns {string} - Valor filtrado
 */
export const filterCitizenship = (value) => {
  if (!value) return '';
  return value.slice(0, 25);;
};

/**
 * Formatea la ciudadanía (aplica el filtro)
 * @param {string} value - Valor a formatear
 * @returns {string} - Valor formateado
 */
export const formatCitizenship = (value) => {
  return filterCitizenship(value);
};

/**
 * Valida el porcentaje de propiedad (1-100, solo números)
 * @param {string} value - Valor a validar
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateOwnershipPercentage = (value, fieldName = 'Ownership Percentage') => {
  if (value === null || value === undefined || value.trim() === '') {
    return {
      isValid: false,
      message: `${fieldName} cannot be empty`
    };
  }
  
  // Solo permitir números
  const numberPattern = /^\d+(\.\d+)?$/;
  if (!numberPattern.test(value)) {
    return {
      isValid: false,
      message: `${fieldName} can only contain numbers`
    };
  }
  
  // Convertir a número y validar rango
  const percentage = parseFloat(value);
  if (isNaN(percentage)) {
    return {
      isValid: false,
      message: `${fieldName} must be a valid number`
    };
  }
  
  if (percentage < 1 || percentage > 100) {
    return {
      isValid: false,
      message: `${fieldName} must be between 1 and 100`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Filtra caracteres no permitidos para porcentaje de propiedad (solo números y punto decimal, máximo 3 dígitos)
 * @param {string} value - Valor a filtrar
 * @returns {string} - Valor filtrado
 */
export const filterOwnershipPercentage = (value) => {
  if (!value) return '';
  
  // Solo permitir números y punto decimal
  let filtered = value.replace(/[^\d.]/g, '');
  
  // Limitar a máximo 3 dígitos enteros
  const parts = filtered.split('.');
  if (parts[0].length > 3) {
    parts[0] = parts[0].substring(0, 3);
  }
  
  // Limitar decimales a máximo 2 dígitos
  if (parts[1] && parts[1].length > 2) {
    parts[1] = parts[1].substring(0, 2);
  }
  
  // Reconstruir el valor
  filtered = parts[0];
  if (parts[1]) {
    filtered += '.' + parts[1];
  }
  
  // Limitar el valor numérico a 100 máximo
  const numericValue = parseFloat(filtered);
  if (!isNaN(numericValue) && numericValue > 100) {
    return '100';
  }
  
  return filtered;
};

/**
 * Formatea el porcentaje de propiedad (aplica el filtro)
 * @param {string} value - Valor a formatear
 * @returns {string} - Valor formateado
 */
export const formatOwnershipPercentage = (value) => {
  return filterOwnershipPercentage(value);
};

/**
 * Valida el Social Security Number (XXX-XX-XXXX)
 * @param {string} value - Valor a validar
 * @param {string} fieldName - Nombre del campo para el mensaje de error
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateSSN = (value, fieldName = 'Social Security Number') => {
  if (value === null || value === undefined || value.trim() === '') {
    return {
      isValid: false,
      message: `${fieldName} cannot be empty`
    };
  }
  
  // Patrón: XXX-XX-XXXX (3 dígitos, guión, 2 dígitos, guión, 4 dígitos)
  const ssnPattern = /^\d{3}-\d{2}-\d{4}$/;
  
  if (!ssnPattern.test(value)) {
    return {
      isValid: false,
      message: `${fieldName} must be in format XXX-XX-XXXX`
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Formatea el Social Security Number (XXX-XX-XXXX)
 * @param {string} value - Valor a formatear
 * @returns {string} - Valor formateado
 */
export const formatSSN = (value) => {
  if (!value) return '';
  
  // Remover todos los caracteres no numéricos
  const digits = value.replace(/\D/g, '');
  
  // Formatear como XXX-XX-XXXX
  let formatted = '';
  if (digits.length >= 3) {
    formatted = digits.substring(0, 3);
    if (digits.length >= 5) {
      formatted += '-' + digits.substring(3, 5);
      if (digits.length >= 9) {
        formatted += '-' + digits.substring(5, 9);
      } else if (digits.length > 5) {
        formatted += '-' + digits.substring(5);
      }
    } else if (digits.length > 3) {
      formatted += '-' + digits.substring(3);
    }
  } else {
    formatted = digits;
  }
  
  return formatted;
};

/**
 * Filtra FICO Score a solo dígitos (máximo 3)
 * @param {string} value
 * @returns {string}
 */
export const filterFicoScore = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 3);
};

/**
 * Formatea el FICO Score (aplica el filtro)
 * @param {string} value - Valor a formatear
 * @returns {string} - Valor formateado
 */
export const formatFicoScore = (value) => {
  return filterFicoScore(value);
};

/**
 * Valida FICO Score (opcional). Si se provee debe estar entre 300 y 850.
 * @param {string} value
 * @param {string} fieldName
 * @returns {{isValid: boolean, message: string}}
 */
export const validateFicoScore = (value, fieldName = 'FICO Score') => {
  // Campo opcional
  if (value === null || value === undefined || value.toString().trim() === '') {
    return { isValid: true, message: '' };
  }

  // Debe ser numérico de hasta 3 dígitos
  const digitsOnly = /^\d{1,3}$/;
  if (!digitsOnly.test(value)) {
    return { isValid: false, message: `${fieldName} must contain up to 3 digits` };
  }

  const score = parseInt(value, 10);
  if (isNaN(score)) {
    return { isValid: false, message: `${fieldName} must be a valid number` };
  }
  if (score < 300 || score > 850) {
    return { isValid: false, message: `${fieldName} must be between 300 and 850` };
  }
  return { isValid: true, message: '' };
};

/**
 * Formatea el Zip Code (5 dígitos + guión opcional + máximo 4 caracteres adicionales)
 * @param {string} value - Valor a formatear
 * @returns {string} - Valor formateado
 */
export const formatZipCode = (value) => {
  if (!value) return '';
  
  // Remover todos los caracteres no alfanuméricos excepto guiones
  const cleanValue = value.replace(/[^A-Za-z0-9-]/g, '');
  
  // Si ya tiene el formato completo (5 caracteres + guión + 4 caracteres), no permitir más
  if (/^[A-Za-z0-9]{5}-[A-Za-z0-9]{4}$/.test(cleanValue)) {
    return cleanValue;
  }
  
  // Si ya tiene 5 caracteres y no tiene guión, agregar guión automáticamente
  if (/^[A-Za-z0-9]{5}$/.test(cleanValue)) {
    return cleanValue;
  }
  
  // Si tiene guión, limitar a 5 + guión + máximo 4
  if (cleanValue.includes('-')) {
    const parts = cleanValue.split('-');
    if (parts[0].length > 5) {
      parts[0] = parts[0].substring(0, 5);
    }
    if (parts[1] && parts[1].length > 4) {
      parts[1] = parts[1].substring(0, 4);
    }
    return parts.join('-');
  }
  
  // Si no tiene guión, limitar a 5 caracteres
  return cleanValue.substring(0, 5);
};

/**
 * Valida el formato del Zip Code (mínimo 5 dígitos, guión opcional, máximo 4 dígitos adicionales)
 * @param {string} value - Valor a validar
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateZipCode = (value) => {
  if (value === null || value === undefined) {
    return {
      isValid: false,
      message: 'Zip Code cannot be null or undefined'
    };
  }
  
  if (value.trim() === '') {
    return {
      isValid: false,
      message: 'Zip Code cannot be empty'
    };
  }
  
  // Patrón: mínimo 5 dígitos, guión opcional, máximo 4 dígitos adicionales (alfanumérico)
  const zipCodePattern = /^[A-Za-z0-9]{5,5}(-[A-Za-z0-9]{1,4})?$/;
  
  if (!zipCodePattern.test(value)) {
    return {
      isValid: false,
      message: 'Zip Code must be 5 digits followed by optional dash and up to 4 additional alphanumeric characters'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};





/**
 * Mantiene el formato de fecha como YYYY-MM-DD
 * @param {string} value - Valor de fecha del input HTML
 * @returns {string} - Fecha en formato YYYY-MM-DD
 */
export const formatDateEstablished = (value) => {
  if (!value) return '';
  
  // Si ya está en formato YYYY-MM-DD, retornar como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  
  // Si está en formato YYYY/MM/DD, convertir a YYYY-MM-DD
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(value)) {
    const [year, month, day] = value.split('/');
    return `${year}-${month}-${day}`;
  }
  
  // Si es un objeto Date válido, convertir a YYYY-MM-DD
  if (value instanceof Date && !isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  return value;
};



/**
 * Valida el formato de fecha (YYYY-MM-DD) y que no sea mayor que la fecha actual
 * @param {string} value - Valor de fecha a validar
 * @returns {object} - Objeto con isValid (boolean) y message (string)
 */
export const validateDateEstablished = (value) => {
  if (value === null || value === undefined) {
    return {
      isValid: false,
      message: 'Date Established cannot be null or undefined'
    };
  }
  
  if (value.trim() === '') {
    return {
      isValid: false,
      message: 'Date Established cannot be empty'
    };
  }
  
  // Patrón: YYYY-MM-DD
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  
  if (!datePattern.test(value)) {
    return {
      isValid: false,
      message: 'Date Established must be in format YYYY-MM-DD'
    };
  }
  
  // Validar que la fecha sea válida
  const [year, month, day] = value.split('-').map(Number);
  const inputDate = new Date(year, month - 1, day); // month - 1 porque Date usa 0-11
  
  if (isNaN(inputDate.getTime())) {
    return {
      isValid: false,
      message: 'Date Established must be a valid date'
    };
  }
  
  // Validar que no sea mayor que la fecha actual
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999); // Establecer al final del día actual
  
  if (inputDate > currentDate) {
    return {
      isValid: false,
      message: 'Date Established cannot be greater than the current date'
    };
  }
  
  return {
    isValid: true,
    message: ''
  };
};

/**
 * Validaciones específicas para Business Information
 */
export const businessInformationValidations = {
  /**
   * Valida que se haya seleccionado un Type (no puede ser la opción por defecto)
   */
  type: (value) => {
    if (value === null || value === undefined || value === '') {
      return {
        isValid: false,
        message: 'Please select a Type'
      };
    }
    
    // Validar que no sea la opción por defecto "Select Type"
    if (value === 'Select Type') {
      return {
        isValid: false,
        message: 'Please select a valid Type'
      };
    }
    
    return {
      isValid: true,
      message: ''
    };
  },
  
  /**
   * Valida el Legal Name
   */
  legalName: (value) => validateRequiredString(value, 255, 'Legal Name'),
  
  /**
   * Valida el DBA Name
   */
  dbaName: (value) => validateRequiredString(value, 255, 'DBA Name'),
  
  /**
   * Valida el Federal Tax ID
   */
  federalTaxId: validateFederalTaxId,
  
  /**
   * Valida que se haya seleccionado una Industry
   */
  industry: (value) => validateSelectOption(value, 'Industry'),
  
  /**
   * Valida el Phone Number (requerido, solo dígitos, máximo 25 caracteres)
   */
  phoneNumber: (value) => {
    if (value === null || value === undefined || value.toString().trim() === '') {
      return {
        isValid: false,
        message: 'Phone Number cannot be empty'
      };
    }
    const digits = /^\d{1,25}$/;
    if (!digits.test(value)) {
      return {
        isValid: false,
        message: 'Phone Number must contain only digits (max 25)'
      };
    }
    return {
      isValid: true,
      message: ''
    };
  },
  
  /**
   * Valida el formato del Website
   */
  website: validateWebsite,
  
  /**
   * Valida que se haya seleccionado un State of Incorporation
   */
  stateOfInc: (value) => validateSelectOption(value, 'State of Incorporation'),
  
  /**
   * Valida que se haya seleccionado un Business Structure
   */
  businessStructure: (value) => validateSelectOption(value, 'Business Structure'),
  
  /**
   * Valida el formato y validez de Date Established
   */
  dateEstablished: validateDateEstablished
};

/**
 * Valida todos los campos del formulario de Business Information
 * @param {object} formData - Objeto con todos los valores del formulario
 * @returns {object} - Objeto con isValid (boolean), errors (object) y hasErrors (boolean)
 */
export const validateBusinessInformationForm = (formData) => {
  const errors = {};
  let hasErrors = false;
  
  // Validar cada campo
  Object.keys(businessInformationValidations).forEach(fieldName => {
    const validation = businessInformationValidations[fieldName];
    const result = validation(formData[fieldName]);
    
    if (!result.isValid) {
      errors[fieldName] = result.message;
      hasErrors = true;
    }
  });
  
  return {
    isValid: !hasErrors,
    errors,
    hasErrors
  };
};

/**
 * =============================
 * Owner Information validations
 * =============================
 */
export const ownerInformationValidations = {
	firstName: (value) => validateOwnerName(value, 'First Name'),
	middleName: (value) => validateOwnerName(value, 'Middle Name'),
	lastName: (value) => validateOwnerName(value, 'Last Name'),
	citizenship: (value) => validateCitizenship(value),
	gender: (value) => validateRequiredString(value, 50, 'Gender'),
	birthDate: (value) => validateBirthDate(value),
	ownershipPercentage: (value) => validateOwnershipPercentage(value),
	ssn: (value) => validateSSN(value),
	ficoScore: (value) => validateFicoScore(value)
};

/**
 * Valida todos los campos del formulario de Owner Information
 * @param {object} formData - Objeto con todos los valores del formulario
 * @returns {object} - Objeto con isValid (boolean), errors (object) y hasErrors (boolean)
 */
export const validateOwnerInformationForm = (formData) => {
	const errors = {};
	let hasErrors = false;
	
	// Validar cada campo
	Object.keys(ownerInformationValidations).forEach(fieldName => {
		const validation = ownerInformationValidations[fieldName];
		const result = validation(formData[fieldName]);
		
		if (!result.isValid) {
			errors[fieldName] = result.message;
			hasErrors = true;
		}
	});
	
	return {
		isValid: !hasErrors,
		errors,
		hasErrors
	};
};

/**
 * =============================
 * Owner Address validations
 * =============================
 */
export const ownerAddressValidations = {
	streetAddress: (value) => validateRequiredString(value, 255, 'Street Address'),
	streetAddress2: (value) => validateStreetAddress2(value),
	city: (value) => validateRequiredString(value, 255, 'City'),
	state: (value) => validateRequiredString(value, 255, 'State'),
	zipCode: (value) => validateZipCode(value),
	country: (value) => validateRequiredString(value, 255, 'Country'),
	phoneNumber: (value) => validateContactPhone(value, 'Phone Number'),
	email: (value) => validateContactEmail(value, 'Email')
};

/**
 * Valida todos los campos de una dirección de owner
 * @param {object} addressData - Objeto con todos los valores de la dirección
 * @returns {object} - Objeto con isValid (boolean), errors (object) y hasErrors (boolean)
 */
export const validateOwnerAddressForm = (addressData) => {
	const errors = {};
	let hasErrors = false;
	
	// Validar cada campo
	Object.keys(ownerAddressValidations).forEach(fieldName => {
		const validation = ownerAddressValidations[fieldName];
		const result = validation(addressData[fieldName]);
		
		if (!result.isValid) {
			errors[fieldName] = result.message;
			hasErrors = true;
		}
	});
	
	return {
		isValid: !hasErrors,
		errors,
		hasErrors
	};
};

/**
 * =============================
 * Owner Contact validations
 * =============================
 */
export const ownerContactValidations = {
	type: (value) => validateRequiredString(value, 50, 'Contact Type'),
	value: (value) => {
		// Validar según el tipo de contacto
		if (!value || value.trim() === '') {
			return { isValid: false, message: 'Contact value is required' };
		}
		
		// Aquí podrías agregar validación específica según el tipo
		// Por ahora, validación básica
		return { isValid: true, message: '' };
	}
};

/**
 * Valida todos los campos de un contacto de owner
 * @param {object} contactData - Objeto con todos los valores del contacto
 * @returns {object} - Objeto con isValid (boolean), errors (object) y hasErrors (boolean)
 */
export const validateOwnerContactForm = (contactData) => {
	const errors = {};
	let hasErrors = false;
	
	// Validar cada campo
	Object.keys(ownerContactValidations).forEach(fieldName => {
		const validation = ownerContactValidations[fieldName];
		const result = validation(contactData[fieldName]);
		
		if (!result.isValid) {
			errors[fieldName] = result.message;
			hasErrors = true;
		}
	});
	
	return {
		isValid: !hasErrors,
		errors,
		hasErrors
	};
};

/**
 * =============================
 * Bank Information validations
 * =============================
 */

/**
 * Valida Bank Name (requerido, máx 255)
 */
export const validateBankName = (value) => {
  return validateRequiredString(value, 255, 'Bank Name');
};

/**
 * Valida Account Name (requerido, máx 255)
 */
export const validateAccountName = (value) => {
  return validateRequiredString(value, 255, 'Account Name');
};

/**
 * Filtra Account Number (solo dígitos, máx 255)
 */
export const filterAccountNumber = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 19);
};

/**
 * Valida Account Number (requerido, solo dígitos, máx 255)
 */
export const validateAccountNumber = (value) => {
  if (value === null || value === undefined || value.toString().trim() === '') {
    return { isValid: false, message: 'Account Number cannot be empty' };
  }
  const numeric = /^\d+$/;
  if (!numeric.test(value)) {
    return { isValid: false, message: 'Account Number must contain only digits' };
  }
  if (value.length > 19) {
    return { isValid: false, message: 'Account Number cannot exceed 19 digits' };
  }
  return { isValid: true, message: '' };
};

/**
 * Filtra Routing Number (solo dígitos, exactamente 9 máximo mientras escribe)
 */
export const filterRoutingNumber = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 9);
};

/**
 * Valida Routing Number (requerido, exactamente 9 dígitos)
 */
export const validateRoutingNumber = (value) => {
  if (value === null || value === undefined || value.toString().trim() === '') {
    return { isValid: false, message: 'Routing Number cannot be empty' };
  }
  const nineDigits = /^\d{9}$/;
  if (!nineDigits.test(value)) {
    return { isValid: false, message: 'Routing Number must be exactly 9 digits' };
  }
  return { isValid: true, message: '' };
};

/**
 * Valida Bank Address (requerido, máx 500)
 */
export const validateBankAddress = (value) => {
  return validateRequiredString(value, 500, 'Bank Address');
};

/**
 * Valida Bank Country (requerido, máx 255)
 */
export const validateBankCountry = (value) => {
  return validateRequiredString(value, 255, 'Bank Country');
};

/**
 * Valida Bank City (requerido, máx 255)
 */
export const validateBankCity = (value) => {
  return validateRequiredString(value, 255, 'Bank City');
};

/**
 * Valida Bank State (requerido, máx 255)
 */
export const validateBankState = (value) => {
  return validateRequiredString(value, 255, 'Bank State');
};

/**
 * =============================
 * Owner Contacts validations
 * =============================
 */

/**
 * Filtra Contact Phone a solo dígitos (máx 14)
 */
export const filterContactPhone = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 14);
};

/**
 * Valida Contact Phone (requerido, 1-14 dígitos)
 */
export const validateContactPhone = (value, fieldName = 'Contact Phone') => {
  if (value === null || value === undefined || value.toString().trim() === '') {
    return { isValid: false, message: `${fieldName} cannot be empty` };
  }
  const digits = /^\d{1,14}$/;
  if (!digits.test(value)) {
    return { isValid: false, message: `${fieldName} must contain 1 to 14 digits` };
  }
  return { isValid: true, message: '' };
};

/**
 * Valida Contact Email con patrón mínimo solicitado
 * Letras o números antes de @, después letras o números, punto y al menos 2 letras
 */
export const validateContactEmail = (value, fieldName = 'Contact Email') => {
  if (value === null || value === undefined || value.toString().trim() === '') {
    return { isValid: false, message: `${fieldName} cannot be empty` };
  }
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  if (!emailPattern.test(value)) {
    return { isValid: false, message: `${fieldName} must be like user@domain.tld (min 2 letters TLD)` };
  }
  return { isValid: true, message: '' };
};

/**
 * Filtra Phone Number para direcciones (solo dígitos, máximo 25)
 */
export const filterAddressPhoneNumber = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 25);
};

/**
 * Valida Phone Number para direcciones (requerido, solo dígitos, máximo 25)
 */
export const validateAddressPhoneNumber = (value, fieldName = 'Phone Number') => {
  if (value === null || value === undefined || value.toString().trim() === '') {
    return { isValid: false, message: `${fieldName} cannot be empty` };
  }
  const digits = /^\d{1,25}$/;
  if (!digits.test(value)) {
    return { isValid: false, message: `${fieldName} must contain only digits (max 25)` };
  }
  return { isValid: true, message: '' };
};

export const validateLegalName = (value) => {
	return validateRequiredString(value, 255, 'Legal Name');
};

export const validateDBAName = (value) => {
	return validateRequiredString(value, 255, 'DBA Name');
};

/**
 * Formatea el SSN con máscara (XXX-XX-XXXX)
 * @param {string} raw - Valor sin formatear
 * @returns {string} - Valor formateado
 */
export const maskSsnInput = (raw) => {
  let value = String(raw || '').replace(/\D/g, '');
  if (value.length > 9) value = value.slice(0, 9);
  if (value.length > 5) {
    return value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5);
  }
  if (value.length > 3) {
    return value.slice(0, 3) + '-' + value.slice(3);
  }
  return value;
};



export default {
  validateRequiredString,
  validateFederalTaxId,
  formatFederalTaxId,
  validateWebsite,
  validateSelectOption,
  validateStreetAddress2,
  validateZipCode,
  formatZipCode,
  formatDateEstablished,
  validateDateEstablished,
  businessInformationValidations,
  validateBusinessInformationForm,
  // Owner validations
  validateOwnerName,
  filterOwnerName,
  formatOwnerName,
  validateBirthDate,
  getMaxBirthDate,
  validateCitizenship,
  filterCitizenship,
  formatCitizenship,
  validateOwnershipPercentage,
  filterOwnershipPercentage,
  formatOwnershipPercentage,
  validateSSN,
  formatSSN,
  maskSsnInput,
  // Bank validations
  validateBankName,
  validateAccountName,
  filterAccountNumber,
  validateAccountNumber,
  filterRoutingNumber,
  validateRoutingNumber,
  validateBankAddress,
  validateBankCountry,
  validateBankCity,
  validateBankState,
  // Owner contacts validations
  filterContactPhone,
  validateContactPhone,
  validateContactEmail,
  validateFicoScore,
  filterFicoScore,
  formatFicoScore,
  // Address phone number validations
  filterAddressPhoneNumber,
  validateAddressPhoneNumber,
  // Owner information validations
  ownerInformationValidations,
  validateOwnerInformationForm,
  // Owner address validations
  ownerAddressValidations,
  validateOwnerAddressForm,
  // Owner contact validations
  ownerContactValidations,
  validateOwnerContactForm
};
